import React, { memo, useCallback, useContext, useMemo, useState, useRef } from 'react';
import { createUseStyles, Text, Icon, Link, useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import Comment from "../Comment";
import Actions from "../Actions";
import Feedback from "../Feedback";
import { CopilotContext } from "../../../Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotChat');
var _ref4 = /*#__PURE__*/React.createElement(Icon, {
  name: "infoOutlined"
});
var _ref5 = /*#__PURE__*/React.createElement(Icon, {
  name: "infoOutlined"
});
const Chat = /*#__PURE__*/memo(_ref => {
  let {
    chatId,
    chatSuggestions,
    conversation,
    conversationId,
    lastError,
    onResend,
    onCopy,
    onEmail,
    onFeedback,
    onVote,
    isDanaWritting
  } = _ref;
  const [feedbackText, setFeedbackText] = useState('');
  const [activeVote, setActiveVote] = useState('');
  const feedbackIdRef = useRef(null);
  const classes = useStyles();
  const theme = useTheme();
  const {
    t
  } = useWeb5Context();
  const {
    getChatState,
    setVote
  } = useContext(CopilotContext);
  const chatState = useMemo(() => getChatState({
    chatId
  }), [getChatState, chatId]);
  const {
    tables,
    reports
  } = chatState;
  const resend = useCallback((interactionId, parentId) => () => {
    return onResend({
      interactionId,
      parentId
    });
  }, [onResend]);
  const handleOnCopy = useCallback(value => () => {
    if (!value.content && value.role === 'user') return null;
    onCopy && onCopy(value);
  }, [onCopy]);
  const getInteractionIndex = useCallback(interactionId => {
    const responsesIds = Object.entries(conversation).reduce((arr, _ref2) => {
      let [key, value] = _ref2;
      if (value.role !== 'user') {
        arr.push(key);
      }
      return arr;
    }, []);
    const index = responsesIds.indexOf(interactionId);
    if (index === -1) return null;
    return index;
  }, [conversation]);
  const keepVote = useCallback(vote => {
    setActiveVote(vote || '');
  }, [setActiveVote]);
  const handleVote = useCallback(interactionId => newVote => () => {
    if (!newVote || !onVote) return;
    const index = getInteractionIndex(interactionId);
    if (index === null) return;
    onVote({
      conversationId,
      index,
      vote: newVote
    }).then(id => {
      feedbackIdRef.current = id;
      setVote({
        chatId,
        interactionId,
        vote: newVote
      });
      keepVote(newVote);
    });
  }, [chatId, conversationId, getInteractionIndex, keepVote, onVote, setVote]);
  const onCloseFeedback = useCallback(() => {
    setFeedbackText('');
    keepVote();
    feedbackIdRef.current = null;
  }, [keepVote]);
  const onConfirmFeedback = useCallback(() => {
    if (!onFeedback) return;
    onFeedback({
      id: feedbackIdRef.current,
      feedback: feedbackText
    }).then(() => {
      onCloseFeedback();
    });
  }, [onFeedback, feedbackText, onCloseFeedback]);
  if (!conversation) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, Object.entries(conversation).map((_ref3, index) => {
    let [key, value] = _ref3;
    if (value.error && lastError === key) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.error,
        key: key
      }, _ref4, /*#__PURE__*/React.createElement(Text, {
        type: "body",
        color: theme.colors.utility.textSecondary
      }, t('label_dana_chat_error_not_sent')), /*#__PURE__*/React.createElement(Link, {
        variation: "primary",
        onClick: resend(key, value.parentId)
      }, t('action_dana_chat_error_try_again')));
    }
    if (value.error && lastError !== key) {
      return /*#__PURE__*/React.createElement("div", {
        className: "dana-chat-conversation-error",
        key: key
      }, _ref5, /*#__PURE__*/React.createElement(Text, {
        type: "body",
        color: theme.colors.utility.textSecondary
      }, t('label_dana_chat_error_not_sent')));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Comment, {
      key: key,
      interactionId: key,
      text: value.content,
      onCopy: onCopy && value.role !== 'user' && handleOnCopy(value),
      onEmail: onEmail,
      isUser: value.role === 'user',
      isProgress: !value.isFinished,
      loading: value.isLoading || false,
      isError: value.isError || false,
      finished: value.isFinished,
      vote: value.vote,
      onVote: !!onVote && !!onFeedback ? handleVote(key) : undefined,
      creationDate: value.creationDate || null,
      disableVote: value.disableVote,
      tables: tables,
      reports: reports
    }), /*#__PURE__*/React.createElement(Actions, {
      actions: value.actions,
      finished: value.isFinished
    }));
  }), !isDanaWritting && chatSuggestions, /*#__PURE__*/React.createElement(Feedback, {
    vote: activeVote,
    value: feedbackText,
    onChange: setFeedbackText,
    onRequestClose: onCloseFeedback,
    onClose: onCloseFeedback,
    onConfirm: onConfirmFeedback
  }));
});
export default Chat;